import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CommentHubAPIError,
  CommentHubNotificationBadge,
  CommentHubNotificationGroup,
  CommentHubNotificationPrefs,
  CommentHubNotificationPrefsPatch,
} from 'shared-code/commenthub-api'
import { logError } from 'shared-code/log'
import { emailTypesFor, type EmailChoice } from '../account/email-choice'
import { AuthContext } from '../contexts/AuthContext'
import { CommentHubAPIContext } from '../contexts/CommentHubAPIContext'

/** `withAuth` stand-in when no commentHub session container is mounted. */
function rejectWithoutSession<T>(): Promise<T> {
  return Promise.reject(new Error('Not authenticated'))
}

// Contract: poll the badge every 60s, and only the badge. The list is fetched
// when the tray opens, never on a timer.
const badgePollMs = 60_000
const pageSize = 20

/**
 * Dispatched on `window` after a successful `/seen` post, so every OTHER `useNotifications` on the
 * page (the header bell and SoundGuys' member hub tab) re-reads its badge and clears the dots the
 * new watermark covers. `detail.seenSeq` is the server's watermark after the post.
 */
export const NOTIFICATIONS_SEEN_EVENT = 'commenthub:notifications-seen'

/**
 * Dispatched on `window` when the reader just did something that writes them a notification (a
 * Follow writes `thread_followed`), so every `useNotifications` on the page re-reads its badge now
 * instead of at the next minute's tick. Carries nothing: the badge read is the source of truth.
 */
export const NOTIFICATIONS_CHANGED_EVENT = 'commenthub:notifications-changed'

export function announceNotificationsChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT))
  }
}

interface SeenDetail {
  source: object
  seenSeq: number
}

function announceSeen(source: object, seenSeq: number): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<SeenDetail>(NOTIFICATIONS_SEEN_EVENT, { detail: { source, seenSeq } })
    )
  }
}

/** Groups at or under a watermark are read (another instance posted /seen). Pure. */
export function clearSeenGroups(
  groups: readonly CommentHubNotificationGroup[],
  seenSeq: number
): readonly CommentHubNotificationGroup[] {
  const covered = (g: CommentHubNotificationGroup): boolean =>
    g.latestSeq <= seenSeq && Boolean(g.unread || g.unreadCount)
  if (!groups.some(covered)) {
    return groups
  }
  return groups.map(g => {
    if (!covered(g)) {
      return g
    }
    return { ...g, unread: false, unreadCount: 0 }
  })
}

export interface NotificationsFilter {
  /** Empty/absent = every type. Sent as the `types` comma list. */
  types?: readonly string[]
  unread?: boolean
}

export interface UseNotificationsResult {
  unread: number
  capped: boolean
  groups: readonly CommentHubNotificationGroup[]
  loading: boolean
  /** Set when the last read failed, so the tray can say so instead of looking empty. */
  failed: boolean
  hasMore: boolean
  /**
   * The groups that were unread at the moment the tray opened.
   *
   * "Unread" cannot be a server query here: opening the tray posts /seen, which
   * is what clears the badge, so by the time the reader presses the chip the
   * server rightly considers all of it read and `unread=true` returns nothing.
   * The snapshot is also *more* complete than that query would now be — the
   * feed is newest-first and unread means "past the watermark", so everything
   * unread is a prefix of the first page.
   */
  unreadKeys: ReadonlySet<string>
  prefs: CommentHubNotificationPrefs | null
  /**
   * Fetch (or refetch) the newest page under `filter`. Replaces the list.
   *
   * `markSeen` moves the reader's watermark to the cursor this page returns and
   * clears the badge. It belongs here rather than on tray-open because at open
   * time there is no cursor yet — the page has to arrive first. Pass it only for
   * the unfiltered load: a filtered page's newest cursor can be lower than the
   * true newest, and while posting a lower one is a safe no-op server-side, it
   * would leave the badge showing.
   */
  loadFirstPage: (filter: NotificationsFilter, markSeen?: boolean) => void
  /** Page backward with `before`. Merges. */
  loadMore: (filter: NotificationsFilter) => void
  loadPrefs: () => void
  /** The Site switch: hide or show a kind (a read-time filter; nothing is deleted). */
  setMuted: (type: string, muted: boolean) => void
  /** An Email switch: mail this kind or not. PUTs `{ emailTypes }` only. */
  setEmailType: (type: string, on: boolean) => void
  /** "Pause all email": PUTs `{ emailPaused }` only. */
  setEmailPaused: (paused: boolean) => void
  /** The one email choice (design 1b): PUTs the whole `{ emailTypes }` list for it. */
  setEmailChoice: (choice: EmailChoice) => void
  /**
   * Forget every site mute, once: the inbox-first page has no site switches, and "everything
   * always appears in this inbox" is only true once a mute set under the old grid is gone (a
   * muted kind is also never emailed). PUTs `{ mutedTypes: [] }` only, and only when some exist.
   */
  clearMutes: () => void
  /**
   * "Mark all read": move the watermark to the newest notification of ANY type. The cursor comes
   * from a fresh unfiltered one-row read, never from the page on screen — a filtered page's
   * newest cursor can be lower than the true newest. Clears the badge and every loaded row's
   * unread flag. Resolves false when it could not be saved.
   */
  markAllSeen: () => Promise<boolean>
}

export interface UseNotificationsOptions {
  /**
   * `false`: read the badge once on mount instead of every 60s. For a second surface on a page
   * whose header already polls (SoundGuys' member hub tab) — one poller per page, not two.
   */
  pollBadge?: boolean
  /**
   * `false`: inert — no token, no request — as without a session. For a surface that is mounted
   * for every reader but shows notifications only to one (SoundGuys' member hub: the member
   * themself); a flag rather than a conditional mount, which would remount the page under it.
   */
  enabled?: boolean
}

/**
 * The merge the contract's overlap window depends on.
 *
 * The `after` read deliberately returns rows behind the cursor, because a row's
 * sequence is assigned at insert but only becomes visible at commit: a poll
 * landing in between would otherwise advance past a notification that then
 * appears *behind* the cursor and is never fetched again — a reply lost for
 * good. The overlap trades that silent loss for a few duplicates, and this is
 * what absorbs them. Groups are idempotent under `groupKey`, so upserting by it
 * collapses duplicates exactly the way deduping by row id would.
 *
 * Never append.
 */
export function mergeNotificationGroups(
  prev: readonly CommentHubNotificationGroup[],
  incoming: readonly CommentHubNotificationGroup[]
): CommentHubNotificationGroup[] {
  const byKey = new Map(prev.map(g => [g.groupKey, g]))
  for (const group of incoming) {
    const existing = byKey.get(group.groupKey)
    // A group's latestSeq only ever moves forward, so this also settles the
    // ordering of two copies of the same group seen on different polls.
    if (!existing || group.latestSeq >= existing.latestSeq) {
      byKey.set(group.groupKey, group)
    }
  }
  return Array.from(byKey.values()).sort((a, b) => b.latestSeq - a.latestSeq)
}

/** The preferences with `patch` applied — the optimistic state a switch shows at once. */
export function applyPrefsPatch(
  prefs: CommentHubNotificationPrefs,
  patch: CommentHubNotificationPrefsPatch
): CommentHubNotificationPrefs {
  const next = { ...prefs }
  if (patch.mutedTypes !== undefined) {
    next.mutedTypes = patch.mutedTypes
  }
  if (patch.emailTypes !== undefined) {
    next.emailTypes = patch.emailTypes
  }
  if (patch.emailPaused !== undefined) {
    next.emailPaused = patch.emailPaused
  }
  return next
}

/** `list` with `type` in (`on`) or out — no duplicate, order kept. */
export function toggleType(list: readonly string[], type: string, on: boolean): string[] {
  return on ? Array.from(new Set([...list, type])) : list.filter(t => t !== type)
}

/**
 * One preferences write, optimistic: show `patch` now, PUT exactly `patch` (the server's PUT is
 * partial — a field sent replaces its value, a field left out keeps its own), take the server's
 * answer, and on a failure put every switch back where the server still has it (`prefs`).
 *
 * `isLatest`: whether this is still the newest write. Two quick flips overlap; the first one's
 * answer (or rollback) predates the second's optimistic state, so applying it would flip the
 * second switch back on screen — and a third flip in that window would PUT a list built from the
 * stale state, losing the second on the server. Only the newest write touches the state; the
 * second write's answer already carries the first's outcome.
 */
export async function savePrefsPatch(
  prefs: CommentHubNotificationPrefs,
  patch: CommentHubNotificationPrefsPatch,
  setPrefs: (prefs: CommentHubNotificationPrefs) => void,
  send: (patch: CommentHubNotificationPrefsPatch) => Promise<CommentHubNotificationPrefs>,
  isLatest: () => boolean = () => true
): Promise<boolean> {
  setPrefs(applyPrefsPatch(prefs, patch))
  try {
    const answer = await send(patch)
    if (isLatest()) {
      setPrefs(answer)
    }
    return true
  } catch (e: unknown) {
    if (isLatest()) {
      setPrefs(prefs)
    }
    logError('Notification preference update failed', e)
    return false
  }
}

/**
 * The reader notification bell's data layer. Mount it only behind a session —
 * an anonymous reader must generate zero requests, so the caller renders the
 * component that owns this hook only when `authState` exists rather than
 * mounting it and short-circuiting inside.
 */
export function useNotifications({
  pollBadge = true,
  enabled = true,
}: UseNotificationsOptions = {}): UseNotificationsResult {
  // useOptionalContainer, not useContainer: the latter THROWS without a
  // provider, and this hook is called from the header — so a layout that
  // forgot the provider would blank the entire page rather than quietly drop
  // notifications. Both apps mount it today; this keeps the blast radius of
  // getting that wrong proportional to the feature.
  const commentHub = CommentHubAPIContext.useOptionalContainer()
  const api = commentHub?.api ?? null
  const site = commentHub?.site
  // Optional for the same reason: without the auth provider this hook is inert (no token,
  // no requests) rather than a throw.
  const session = AuthContext.useOptionalContainer()
  const authState = session?.authState ?? null
  const withAuth = session?.withAuth ?? rejectWithoutSession
  const token = enabled ? authState?.token ?? null : null

  // AuthContext rebuilds `withAuth` on every render of the provider, so using it
  // directly as a useCallback dependency would rebuild every action below on
  // every auth re-render — and the tray's "load when the filter changes" effect
  // would fire again each time, refetching for no reason. The ref keeps the
  // actions stable while still calling through to the current implementation.
  const withAuthRef = useRef(withAuth)
  withAuthRef.current = withAuth

  const [badge, setBadge] = useState<CommentHubNotificationBadge>({ unread: 0, capped: false })
  const [groups, setGroups] = useState<readonly CommentHubNotificationGroup[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [prefs, setPrefs] = useState<CommentHubNotificationPrefs | null>(null)
  /** The newest preferences write — an older one's answer must not overwrite a newer flip. */
  const prefsWriteRef = useRef(0)
  const [unreadKeys, setUnreadKeys] = useState<ReadonlySet<string>>(new Set())

  // Highest cursor ever seen, which is what /seen is posted. Kept in a ref
  // rather than state: the watermark is not rendered, and a re-render between
  // "page arrived" and "post the cursor" would post a stale one.
  const latestCursorRef = useRef<string | null>(null)
  // Set when the badge poll gets a 401. The poller then stops for good instead
  // of retrying a dead token every minute; the next user-initiated call goes
  // through withAuth and clears the session properly.
  const pollDeadRef = useRef(false)
  // This instance's identity in NOTIFICATIONS_SEEN_EVENT — it ignores its own announcements.
  const selfRef = useRef({})

  useEffect(() => {
    // A new session is a new watermark and a new feed.
    latestCursorRef.current = null
    pollDeadRef.current = false
    setGroups([])
    setNextCursor(null)
    setUnreadKeys(new Set())
    setBadge({ unread: 0, capped: false })
  }, [token])

  /**
   * Badge poll. Deliberately NOT routed through `withAuth`: that helper opens
   * the login dialog on a 401, which is right for something the reader just
   * clicked and wrong for a background timer — someone who left a tab open
   * overnight should not come back to a modal they never asked for.
   */
  useEffect(() => {
    if (!api || !token) {
      return undefined
    }

    let cancelled = false
    let controller: AbortController | null = null

    const poll = (): void => {
      if (cancelled || pollDeadRef.current || document.visibilityState !== 'visible') {
        return
      }
      controller?.abort()
      controller = new AbortController()
      void api
        .getNotificationBadge(token, controller.signal, site || undefined)
        .then(next => {
          if (!cancelled) {
            setBadge(next)
          }
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') {
            return
          }
          if (e instanceof CommentHubAPIError && e.status === 401) {
            pollDeadRef.current = true
            return
          }
          // 429 and transport errors: skip this tick. The next one is a minute
          // away, which is already the backoff the contract asks for.
          logError('Notification badge poll failed', e)
        })
    }

    poll()
    // `pollBadge: false` drops only the timer: the read on becoming visible stays, so a page
    // opened in a background tab still gets its one read when the reader switches to it.
    const timer = pollBadge ? setInterval(poll, badgePollMs) : null
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        poll()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    // Another instance moved the watermark: re-read the badge now, not at the next tick, and
    // clear the dots the new watermark covers.
    const onSeen = (e: Event): void => {
      const { detail } = e as CustomEvent<SeenDetail>
      if (!detail || detail.source === selfRef.current) {
        return
      }
      setGroups(prev => clearSeenGroups(prev, detail.seenSeq))
      poll()
    }
    window.addEventListener(NOTIFICATIONS_SEEN_EVENT, onSeen)
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, poll)

    return () => {
      cancelled = true
      controller?.abort()
      if (timer) {
        clearInterval(timer)
      }
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener(NOTIFICATIONS_SEEN_EVENT, onSeen)
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, poll)
    }
  }, [api, token, pollBadge, site])

  const loadFirstPage = useCallback(
    (filter: NotificationsFilter, markSeen = false) => {
      if (!api || !token) {
        return
      }
      setLoading(true)
      setFailed(false)
      void withAuthRef
        .current(t =>
          api.listNotifications(t, {
            limit: pageSize,
            site: site || undefined,
            types: filter.types?.length ? filter.types : undefined,
            unread: filter.unread,
          })
        )
        .then(page => {
          // A filter change is a different set, so this replaces rather than
          // merges — merging would leave rows the new filter excludes on screen.
          setGroups(mergeNotificationGroups([], page.groups))
          setNextCursor(page.nextCursor)
          if (markSeen) {
            // Captured before /seen is posted below — after that the flag is
            // gone from the server's point of view.
            setUnreadKeys(new Set(page.groups.filter(g => g.unread).map(g => g.groupKey)))
          }
          if (page.latestCursor) {
            latestCursorRef.current = page.latestCursor
            if (markSeen) {
              // Optimistic: the watermark only ever moves forward server-side,
              // so a failed post costs one stale badge tick, not a wrong one.
              setBadge({ unread: 0, capped: false })
              void withAuthRef
                .current(t => api.markNotificationsSeen(t, page.latestCursor!))
                .then(res => announceSeen(selfRef.current, res.seenSeq))
                .catch((e: unknown) => {
                  logError('Notification seen failed', e)
                })
            }
          }
        })
        .catch((e: unknown) => {
          setFailed(true)
          logError('Notification list failed', e)
        })
        .finally(() => setLoading(false))
    },
    [api, token, site]
  )

  const loadMore = useCallback(
    (filter: NotificationsFilter) => {
      if (!api || !token || !nextCursor) {
        return
      }
      setLoading(true)
      void withAuthRef
        .current(t =>
          api.listNotifications(t, {
            before: nextCursor,
            limit: pageSize,
            site: site || undefined,
            types: filter.types?.length ? filter.types : undefined,
            unread: filter.unread,
          })
        )
        .then(page => {
          setGroups(prev => mergeNotificationGroups(prev, page.groups))
          setNextCursor(page.nextCursor)
        })
        .catch((e: unknown) => {
          setFailed(true)
          logError('Notification paging failed', e)
        })
        .finally(() => setLoading(false))
    },
    [api, token, site, nextCursor]
  )

  const loadPrefs = useCallback(() => {
    if (!api || !token || prefs) {
      return
    }
    void withAuthRef
      .current(t => api.getNotificationPrefs(t))
      .then(setPrefs)
      .catch((e: unknown) => {
        logError('Notification preferences failed', e)
      })
  }, [api, token, prefs])

  const updatePrefs = useCallback(
    (patch: CommentHubNotificationPrefsPatch) => {
      if (!api || !token || !prefs) {
        return
      }
      const write = ++prefsWriteRef.current
      void savePrefsPatch(
        prefs,
        patch,
        setPrefs,
        p => withAuthRef.current(t => api.updateNotificationPrefs(t, p)),
        () => prefsWriteRef.current === write
      )
    },
    [api, token, prefs]
  )

  // Each switch sends its own field only (the PUT is partial); a list goes whole.
  const setMuted = useCallback(
    (type: string, muted: boolean) => {
      if (prefs) {
        updatePrefs({ mutedTypes: toggleType(prefs.mutedTypes, type, muted) })
      }
    },
    [prefs, updatePrefs]
  )

  const setEmailType = useCallback(
    (type: string, on: boolean) => {
      if (prefs) {
        updatePrefs({ emailTypes: toggleType(prefs.emailTypes ?? [], type, on) })
      }
    },
    [prefs, updatePrefs]
  )

  const setEmailPaused = useCallback(
    (paused: boolean) => updatePrefs({ emailPaused: paused }),
    [updatePrefs]
  )

  const setEmailChoice = useCallback(
    (choice: EmailChoice) => {
      if (prefs) {
        updatePrefs({ emailTypes: emailTypesFor(choice, prefs.emailAvailableTypes ?? []) })
      }
    },
    [prefs, updatePrefs]
  )

  const clearMutes = useCallback(() => {
    if (prefs && prefs.mutedTypes.length > 0) {
      updatePrefs({ mutedTypes: [] })
    }
  }, [prefs, updatePrefs])

  const markAllSeen = useCallback(async (): Promise<boolean> => {
    if (!api || !token) {
      return false
    }
    try {
      const newest = await withAuthRef.current(t =>
        api.listNotifications(t, { limit: 1, site: site || undefined })
      )
      if (newest.latestCursor) {
        latestCursorRef.current = newest.latestCursor
        const res = await withAuthRef.current(t =>
          api.markNotificationsSeen(t, newest.latestCursor!)
        )
        announceSeen(selfRef.current, res.seenSeq)
      }
      setBadge({ unread: 0, capped: false })
      setGroups(prev =>
        prev.map(g => (g.unread || g.unreadCount ? { ...g, unread: false, unreadCount: 0 } : g))
      )
      // The reader asked for everything read: the open-time capture (7971e3be7) is spent, so the
      // tray's "Unread · N" and its Unread view empty too. Only on success — a failed post keeps
      // what was unread on open.
      setUnreadKeys(new Set())
      return true
    } catch (e: unknown) {
      logError('Notification mark all read failed', e)
      return false
    }
  }, [api, token, site])

  return {
    unread: badge.unread,
    capped: badge.capped,
    groups,
    loading,
    failed,
    hasMore: Boolean(nextCursor),
    unreadKeys,
    prefs,
    loadFirstPage,
    loadMore,
    loadPrefs,
    setMuted,
    setEmailType,
    setEmailPaused,
    setEmailChoice,
    clearMutes,
    markAllSeen,
  }
}
